import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { PaginatedList } from "libs/models/paginated-list";
import { EqualFilter, Filter } from "libs/models/filter";
import { FilterTab } from "components/tab-filter/tab-filter.props";
import { BranchUserModel, UserRequestModel } from "libs/models/user-model";
import { AsyncAppThunk } from "store/application-state";
import { addSnack, showLoaderWhile } from "./ui";
import { getFilterQuery } from "utils/filter";
import { validateAddUserState } from "pages/AddUser/add-user.policy";
import { validateEditUserState } from "pages/EditUser/edit-user.policy";
import {
  addUserService,
  editUserService,
  getUserDetailsByIdService,
  getUsersService,
} from "services/crosspay-service";
import { CustomizedBack } from "store/navigation-wrapper";
import translate from "utils/translations";

export interface UserStore {
  isValidating: boolean;
  newUserData: UserRequestModel;
  updateUserData: UserRequestModel;
  usersData: PaginatedList<UserRequestModel>;
  filter: Filter;
  searchingValue: string | undefined;
  tabFilterValue: FilterTab | undefined;
  isDialogOpen: boolean;
}

const initialState: UserStore = {
  isValidating: false,
  isDialogOpen: false,
  updateUserData: {} as UserRequestModel,
  newUserData: {} as UserRequestModel,
  usersData: { list: [], pageSize: 10, pageIndex: 0, totalCount: 0 },
  filter: {
    orderBy: { key: "FirstName", sort: "asc" },
  },
  searchingValue: undefined,
  tabFilterValue: undefined,
};

const slice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setBranchUsersFetchingData: (
      state: UserStore,
      action: PayloadAction<PaginatedList<UserRequestModel>>,
    ) => {
      state.usersData = action.payload;
    },
    setIsValidating: (state: UserStore, action: PayloadAction<boolean>) => {
      state.isValidating = action.payload;
    },
    setEqualFilter: (
      state: UserStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      if (value === null) {
        delete state.filter[key];
        return state;
      } else {
        state.filter = { ...state.filter, [key]: value! };
      }
      return state;
    },
    setUsersViewDynamicSearchFilter: (
      state: UserStore,
      action: PayloadAction<EqualFilter[]>,
    ) => {
      state.filter = { ...state.filter, or: action.payload };
    },
    setSearchKey: (state: UserStore, action: PayloadAction<string>) => {
      state.searchingValue = action.payload;
    },
    setUsersPage: (state: UserStore, action: PayloadAction<number>) => {
      state.usersData.pageIndex = action.payload;
    },

    setNewUserDataProps: (
      state: UserStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        newUserData: { ...state.newUserData, [key]: value! },
      };
    },
    setUpdateUserData: (
      state: UserStore,
      action: PayloadAction<UserRequestModel>,
    ) => {
      state.updateUserData = action.payload;
    },
    setUpdateUserDataProps: (
      state: UserStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        updateUserData: { ...state.updateUserData, [key]: value! },
      };
    },
    setTabFilterChange: (
      state: UserStore,
      action: PayloadAction<FilterTab>,
    ) => {
      state.tabFilterValue = action.payload;
    },

    addingUser: (state: UserStore) => {
      state.newUserData = initialState.newUserData;
      state.isValidating = false;
    },
    editingUser: (state: UserStore) => {
      state.updateUserData = initialState.updateUserData;
      state.isValidating = false;
    },
    clearFilters: (state: UserStore) => {
      state.filter = initialState.filter;
      state.searchingValue = undefined;
    },
  },
});

export const {
  setBranchUsersFetchingData,
  setEqualFilter,
  setUsersViewDynamicSearchFilter,
  setSearchKey,
  setTabFilterChange,
  setUsersPage,
  setNewUserDataProps,
  addingUser,
  setIsValidating,
  setUpdateUserDataProps,
  setUpdateUserData,
  editingUser,
  clearFilters,
} = slice.actions;
export const reducer = slice.reducer;

export const addUser = (): AsyncAppThunk => async (dispatch, store) => {
  var userState = store().user;
  dispatch(setIsValidating(true));
  if (Object.keys(validateAddUserState(store().user)).length === 0) {
    dispatch(
      showLoaderWhile(async () => {
        dispatch(setIsValidating(false));
        const response = await addUserService(userState.newUserData);
        if (
          response &&
          Object.keys(response).length > 0 &&
          response.result !== null
        ) {
          CustomizedBack();
          dispatch(addingUser());
          dispatch(
            addSnack({
              message: translate(
                "User_Added_Successfully",
                "User Added Successfully",
              ),
            }),
          );
        }
      }),
    );
  }
};

export const editUser = (): AsyncAppThunk => async (dispatch, store) => {
  var userState = store().user;
  dispatch(setIsValidating(true));
  if (Object.keys(validateEditUserState(store().user)).length === 0) {
    dispatch(
      showLoaderWhile(async () => {
        dispatch(setIsValidating(false));
        const response = await editUserService(userState.updateUserData);
        if (
          response &&
          Object.keys(response).length > 0 &&
          response.result !== null
        ) {
          CustomizedBack();
          dispatch(editingUser());
          dispatch(
            addSnack({
              message: translate(
                "User_Edited_Successfully",
                "User Edited Successfully",
              ),
            }),
          );
        }
      }),
    );
  }
};
export const getUsersData = (): AsyncAppThunk => async (dispatch, store) => {
  dispatch(
    showLoaderWhile(async () => {
      var userPaging = store().user.usersData;
      var queryOptions = getFilterQuery(
        store().user.filter,
        userPaging.pageIndex * userPaging.pageSize,
        userPaging.pageSize,
      );
      const apiResponse = await getUsersService(queryOptions);
      if (
        apiResponse &&
        Object.keys(apiResponse).length > 0 &&
        apiResponse.result !== null
      ) {
        dispatch(setBranchUsersFetchingData(apiResponse.result));
      }
    }),
  );
};
export const getUserDataById =
  (id: string): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await getUserDetailsByIdService(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setUpdateUserData(apiResponse.result));
        }
      }),
    );
  };
